import type { DraggableAttributes, DraggableSyntheticListeners } from "@dnd-kit/core";
import { memo, useState } from "react";
import { useNavigate } from "react-router";
import type { Item } from "../api/types";
import { useT } from "../i18n";
import { colorHex } from "../lib/colors";
import { deadlineLabel, dueLabel } from "../lib/dates";
import { renderInline, stripMarkdown } from "../lib/markdown";
import { subtaskCounts } from "../lib/tasks";
import { useApp, useSyncState } from "../store";
import { completeTask, deleteTask, moveTaskToProject, uncompleteTask, updateTask } from "../sync/actions";
import {
  IconBell,
  IconCalendar,
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconComment,
  IconDrag,
  IconEdit,
  IconFlag,
  IconHash,
  IconInbox,
  IconLabel,
  IconMore,
  IconRepeat,
  IconSubtask,
  IconTarget,
  IconTrash,
} from "../ui/icons";
import { DatePicker, PriorityMenu, ProjectPicker } from "../ui/pickers";
import { usePrefs } from "../ui/prefs";
import { Avatar, Confirm, Popover, useAnchor } from "../ui/primitives";

export function Checkbox({
  priority,
  checked,
  onToggle,
  label,
}: {
  priority: number;
  checked: boolean;
  onToggle: () => void;
  label: string;
}) {
  const cls = priority === 4 ? "p1" : priority === 3 ? "p2" : priority === 2 ? "p3" : "p4";
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      className={`checkbox ${cls}${checked ? " checked" : ""}`}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
    >
      <IconCheck size={12} />
    </button>
  );
}

export function openTask(navigate: ReturnType<typeof useNavigate>, id: string) {
  const url = new URL(window.location.href);
  url.searchParams.set("task", id);
  navigate(`${url.pathname}${url.search}`);
}

export interface TaskRowProps {
  item: Item;
  showProject?: boolean;
  /** Hide the date chip (e.g. inside a day group of Upcoming). */
  hideDate?: boolean;
  dragHandle?: { attributes: DraggableAttributes; listeners: DraggableSyntheticListeners } | null;
  hasChildren?: boolean;
  dragging?: boolean;
}

export const TaskRow = memo(function TaskRow({
  item,
  showProject,
  hideDate,
  dragHandle,
  hasChildren,
  dragging,
}: TaskRowProps) {
  const state = useSyncState();
  const prefs = usePrefs();
  const t = useT();
  const navigate = useNavigate();
  const focused = useApp((s) => s.focusedTaskId === item.id);
  const [completing, setCompleting] = useState(false);
  const date = useAnchor();
  const more = useAnchor();
  const prio = useAnchor();
  const move = useAnchor();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const project = state.projects[item.project_id];
  const section = item.section_id ? state.sections[item.section_id] : null;
  const sub = subtaskCounts(state, item.id);
  const notes = item.note_count ?? Object.values(state.notes).filter((n) => n.item_id === item.id).length;
  const reminders = Object.values(state.reminders).some((r) => r.item_id === item.id);
  const assignee = item.responsible_uid ? state.collaborators[item.responsible_uid] : null;
  const due = item.due ? dueLabel(item.due, prefs) : null;
  const deadline = item.deadline ? deadlineLabel(item.deadline, prefs) : null;

  const toggle = () => {
    if (item.checked) {
      void uncompleteTask(item.id);
      return;
    }
    setCompleting(true);
    setTimeout(() => {
      completeTask(item, () => setCompleting(false));
      if (item.due?.is_recurring) setCompleting(false);
    }, 380);
  };

  const anyOpen = Boolean(date.anchor || more.anchor || prio.anchor || move.anchor);

  return (
    <div
      className={`task-row${focused ? " focused" : ""}${completing ? " completing" : ""}${dragging ? " dragging" : ""}`}
      data-task-id={item.id}
      data-testid="task-row"
      onClick={() => {
        useApp.getState().set({ focusedTaskId: item.id });
        openTask(navigate, item.id);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") openTask(navigate, item.id);
      }}
      role="listitem"
      aria-label={stripMarkdown(item.content)}
    >
      {dragHandle && (
        <span
          className="drag-handle"
          {...dragHandle.attributes}
          {...dragHandle.listeners}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={() => undefined}
        >
          <IconDrag size={16} />
        </span>
      )}
      {hasChildren && (
        <button
          type="button"
          className="tree-toggle collapse-toggle"
          aria-label={item.is_collapsed ? t("expand") : t("collapse")}
          onClick={(e) => {
            e.stopPropagation();
            void updateTask(item.id, { is_collapsed: !item.is_collapsed });
          }}
        >
          {item.is_collapsed ? <IconChevronRight size={16} /> : <IconChevronDown size={16} />}
        </button>
      )}
      <Checkbox
        priority={item.priority}
        checked={item.checked || completing}
        onToggle={toggle}
        label={t("completed")}
      />
      <div className="task-body">
        <div className={`task-content${item.checked ? " done" : ""}`}>{renderInline(item.content)}</div>
        {item.description && <div className="task-desc">{stripMarkdown(item.description.split("\n")[0] ?? "")}</div>}
        <div className="task-meta">
          {due && !hideDate && (
            <span className={`meta-item ${due.tone}`} data-testid="due-chip">
              {item.due?.is_recurring ? <IconRepeat size={14} /> : <IconCalendar size={14} />}
              {due.text}
            </span>
          )}
          {deadline && (
            <span className={`meta-item ${deadline.tone === "overdue" ? "overdue" : ""}`}>
              <IconTarget size={14} />
              {deadline.text}
            </span>
          )}
          {sub.total > 0 && (
            <span className="meta-item">
              <IconSubtask size={14} />
              {sub.done}/{sub.total}
            </span>
          )}
          {notes > 0 && (
            <span className="meta-item">
              <IconComment size={14} />
              {notes}
            </span>
          )}
          {reminders && (
            <span className="meta-item">
              <IconBell size={14} />
            </span>
          )}
          {item.labels.map((l) => {
            const label = Object.values(state.labels).find((x) => x.name === l);
            return (
              <span key={l} className="meta-item label-chip" style={{ color: colorHex(label?.color) }}>
                <IconLabel size={14} />
                <span style={{ color: "var(--text-2)" }}>{l}</span>
              </span>
            );
          })}
          {showProject && project && (
            <span className="meta-item meta-project">
              {project.inbox_project ? t("inbox") : project.name}
              {section ? ` / ${section.name}` : ""}
              {project.inbox_project ? (
                <IconInbox size={14} />
              ) : (
                <span style={{ color: colorHex(project.color) }}>
                  <IconHash size={14} />
                </span>
              )}
            </span>
          )}
        </div>
      </div>
      {assignee && (
        <div className="task-right">
          <Avatar name={assignee.full_name} size={22} />
        </div>
      )}
      <div className={`task-actions${anyOpen ? " open" : ""}`}>
        <button
          type="button"
          className="icon-btn"
          aria-label={t("edit")}
          onClick={(e) => {
            e.stopPropagation();
            openTask(navigate, item.id);
          }}
        >
          <IconEdit size={18} />
        </button>
        <button
          type="button"
          className="icon-btn"
          aria-label={t("setDate")}
          data-testid="row-date"
          onClick={(e) => {
            e.stopPropagation();
            date.open(e);
          }}
        >
          <IconCalendar size={18} />
        </button>
        <button
          type="button"
          className="icon-btn"
          aria-label={t("comment")}
          onClick={(e) => {
            e.stopPropagation();
            openTask(navigate, item.id);
          }}
        >
          <IconComment size={18} />
        </button>
        <button
          type="button"
          className="icon-btn"
          aria-label={t("more")}
          data-testid="row-more"
          onClick={(e) => {
            e.stopPropagation();
            more.open(e);
          }}
        >
          <IconMore size={18} />
        </button>
      </div>
      {date.anchor && (
        <DatePicker
          anchor={date.anchor}
          onClose={date.close}
          value={item.due}
          onChange={(d) => void updateTask(item.id, { due: d })}
        />
      )}
      {more.anchor && (
        <Popover anchor={more.anchor} onClose={more.close} placement="bottom-end">
          <div className="menu-title">{t("priority")}</div>
          <div className="menu-row">
            {[4, 3, 2, 1].map((p) => (
              <button
                key={p}
                type="button"
                className={`icon-btn${item.priority === p ? " active" : ""}`}
                aria-label={t("priorityN", { n: 5 - p })}
                style={{
                  color: `var(--p${5 - p})`,
                  border: item.priority === p ? "1px solid var(--border)" : undefined,
                }}
                onClick={() => {
                  more.close();
                  void updateTask(item.id, { priority: p });
                }}
              >
                <IconFlag size={18} filled={p !== 1} />
              </button>
            ))}
          </div>
          <div className="menu-sep" />
          <button
            type="button"
            className="menu-item"
            onClick={(e) => {
              const el = more.anchor;
              more.close();
              move.openAt(el);
              e.stopPropagation();
            }}
          >
            <IconHash size={18} />
            {t("moveTo")}
          </button>
          <button
            type="button"
            className="menu-item"
            onClick={() => {
              more.close();
              void navigator.clipboard?.writeText(`${window.location.origin}/app/task/${item.id}`);
            }}
          >
            <IconComment size={18} />
            {t("copyLink")}
          </button>
          <div className="menu-sep" />
          <button
            type="button"
            className="menu-item danger"
            onClick={() => {
              more.close();
              setConfirmDelete(true);
            }}
          >
            <IconTrash size={18} />
            {t("delete")}
          </button>
        </Popover>
      )}
      {prio.anchor && (
        <PriorityMenu
          anchor={prio.anchor}
          onClose={prio.close}
          value={item.priority}
          onChange={(p) => void updateTask(item.id, { priority: p })}
        />
      )}
      {move.anchor && (
        <ProjectPicker
          anchor={move.anchor}
          onClose={move.close}
          value={{ projectId: item.project_id, sectionId: item.section_id }}
          onChange={(target) => void moveTaskToProject(item.id, target.projectId, target.sectionId)}
        />
      )}
      {confirmDelete && (
        <Confirm
          message={t("deleteTaskConfirm")}
          confirmLabel={t("delete")}
          cancelLabel={t("cancel")}
          onConfirm={() => void deleteTask(item.id)}
          onClose={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
});
